import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import { useLanguage, LanguageSwitcher } from "./context/LanguageContext";

// ─── Config from .env ─────────────────────────────────────────────────────────
const YT_URL = process.env.REACT_APP_YT_URL || "https://youtube.com/@tanyafashionskincare";
const IG_URL_MAIN = process.env.REACT_APP_IG_URL_MAIN || "http://instagram.com/tanyafashionskincare/";
const IG_URL_VLOG = process.env.REACT_APP_IG_URL_VLOG || "http://instagram.com/tanikhanvlog1996/";
const YT_COUNT = process.env.REACT_APP_YT_COUNT || "475K+";
const IG_COUNT = process.env.REACT_APP_IG_COUNT || "135K+";
const COMMUNITY_COUNT_HI = process.env.REACT_APP_COMMUNITY_COUNT_HI || "4.80 लाख+";
const COMMUNITY_COUNT_UR = process.env.REACT_APP_COMMUNITY_COUNT_UR || "4.80 لاکھ+";

// ─── Translations ─────────────────────────────────────────────────────────────
const T = {
  en: {
    siteName: "Tanya Fashion Skincare",
    siteTagline: "DIY Beauty Remedies",
    navHome: "Home",
    navCategories: "Categories",
    navPopular: "Popular",
    navBack: "Back to Home",
    heroBadge: "50+ Proven Natural Remedies",
    heroTitle: "Natural Beauty Remedies That",
    heroHighlight: "Actually Work!",
    heroDesc: `Transform your skin and hair with time-tested DIY remedies using simple kitchen ingredients. No expensive products needed — just nature's finest, proven by ${YT_COUNT} community members!`,
    tags: ["100% Natural", "Kitchen Ingredients", "Proven Results", "Zero Side Effects"],
    btnYT: "Watch Tutorials on YouTube",
    btnAI: "Try AI Face Analysis",
    stat1: "50+",
    stat1L: "DIY Remedies",
    stat2: YT_COUNT,
    stat2L: "Happy Community",
    stat3: "95%",
    stat3L: "Success Rate",
    stat4: "100%",
    stat4L: "Natural & Safe",
    browseTitle: "Browse by Category",
    cats: [
      { id: "all", name: "All Remedies", count: "50+" },
      { id: "skin", name: "Skin Care", count: "20+" },
      { id: "hair", name: "Hair Care", count: "15+" },
      { id: "acne", name: "Acne & Pimples", count: "10+" },
      { id: "anti-aging", name: "Anti-Aging", count: "8+" },
    ],
    collTitle: "Complete DIY Remedy Collection",
    collDesc: "Step-by-step guides with exact ingredients, timing and difficulty for every remedy.",
    remediesAvail: "remedies available",
    ingLabel: "Ingredients",
    benLabel: "Benefit",
    howLabel: "How to",
    diffBeginner: "Beginner",
    diffEasy: "Easy",
    diffMedium: "Medium",
    popularTitle: "Most Popular Remedies",
    popularDesc: "Tried and loved by thousands of community members",
    seasonalTitle: "Seasonal Remedies",
    seasonalDesc: "Special formulations for every Indian season",
    seasons: ["Winter Special", "Summer Cooling", "Monsoon Care"],
    tipsTitle: "Pro Tips & Tricks",
    tipsDesc: "Expert advice to maximize your DIY beauty results",
    tips: [
      {
        icon: "🧪",
        title: "Always Patch Test First",
        desc: "Apply a small amount on your inner wrist or behind the ear and wait 24 hours before using on your face, especially with lemon, cinnamon, or turmeric.",
      },
      {
        icon: "🕐",
        title: "Evening is Best",
        desc: "Apply masks and serums at night when your skin is in repair mode. Ingredients like lemon and turmeric can cause photosensitivity in sunlight.",
      },
      {
        icon: "🌡️",
        title: "Refrigerate DIY Products",
        desc: "Homemade products contain no preservatives. Store in airtight glass containers in the fridge and use within 3–5 days for freshness and safety.",
      },
      {
        icon: "💧",
        title: "Hydrate Inside Out",
        desc: "Drink at least 8 glasses of water daily. No topical remedy works well on dehydrated skin — internal hydration multiplies visible results.",
      },
      {
        icon: "🔄",
        title: "Consistency is Key",
        desc: "DIY remedies are gentle and natural — they need 2–4 weeks of consistent use to show results. Don't switch remedies too quickly.",
      },
      {
        icon: "🌿",
        title: "Fresh Ingredients Only",
        desc: "Always use fresh, organic ingredients when possible. Stale or processed versions of honey, aloe, or fruits lose their active compounds.",
      },
    ],
    ctaTitle: "Ready to Transform Your Beauty Routine?",
    ctaDesc: `Join ${YT_COUNT} people who've discovered the power of natural beauty with proven DIY remedies and video tutorials!`,
    btnSubYT: "Subscribe for New Tutorials",
    btnFollowIG: "Follow for Daily Tips",
    newsTitle: "Get Weekly DIY Tips & Exclusive Recipes",
    newsDesc: "Join our newsletter for the latest natural beauty secrets!",
    newsPlaceholder: "Enter your email address",
    btnSub: "Subscribe",
    subDone: "🎉 You're subscribed! Thank you.",
    emailErr: "Please enter a valid email address.",
    testTitle: "What Our Community Says",
    testDesc: "Real results from real people across India",
    usedLabel: "Used",
    disclaimerStrong: "⚠️ Important Disclaimer:",
    disclaimerText: "These DIY remedies are based on traditional Ayurvedic knowledge and personal experience. Always",
    patchTest: "perform a patch test",
    disclaimerEnd:
      "24 hours before full application. Avoid citrus remedies before sun exposure. If you have sensitive skin, allergies, or any skin condition, please consult a certified dermatologist before use.",
    footerTagline: `Natural Beauty • DIY Remedies • ${YT_COUNT} Community`,
    footerYT: `YouTube (${YT_COUNT})`,
    footerIG: `Instagram (${IG_COUNT})`,
    footerHome: "Main Website",
    footerCopy: "Made with 💖 for natural beauty lovers",
  },

  hi: {
    siteName: "तान्या फैशन स्किनकेयर",
    siteTagline: "DIY सौंदर्य उपाय",
    navHome: "होम",
    navCategories: "श्रेणियाँ",
    navPopular: "लोकप्रिय",
    navBack: "होम पर वापस",
    heroBadge: "50+ सिद्ध प्राकृतिक उपाय",
    heroTitle: "प्राकृतिक सौंदर्य उपाय जो",
    heroHighlight: "वास्तव में काम करते हैं!",
    heroDesc: `सरल रसोई सामग्री से अपनी त्वचा और बालों को बदलें। कोई महंगे उत्पाद नहीं — ${COMMUNITY_COUNT_HI} समुदाय सदस्यों द्वारा सिद्ध!`,
    tags: ["100% प्राकृतिक", "रसोई सामग्री", "सिद्ध परिणाम", "शून्य दुष्प्रभाव"],
    btnYT: "YouTube पर ट्यूटोरियल देखें",
    btnAI: "AI फेस विश्लेषण करें",
    stat1: "50+",
    stat1L: "DIY उपाय",
    stat2: COMMUNITY_COUNT_HI,
    stat2L: "खुश समुदाय",
    stat3: "95%",
    stat3L: "सफलता दर",
    stat4: "100%",
    stat4L: "प्राकृतिक व सुरक्षित",
    browseTitle: "श्रेणी के अनुसार खोजें",
    cats: [
      { id: "all", name: "सभी उपाय", count: "50+" },
      { id: "skin", name: "त्वचा देखभाल", count: "20+" },
      { id: "hair", name: "बाल देखभाल", count: "15+" },
      { id: "acne", name: "मुहांसे व पिंपल्स", count: "10+" },
      { id: "anti-aging", name: "एंटी-एजिंग", count: "8+" },
    ],
    collTitle: "पूर्ण DIY उपाय संग्रह",
    collDesc: "हर उपाय के लिए सटीक सामग्री, समय और कठिनाई के साथ चरण-दर-चरण मार्गदर्शिका।",
    remediesAvail: "उपाय उपलब्ध",
    ingLabel: "सामग्री",
    benLabel: "लाभ",
    howLabel: "विधि",
    diffBeginner: "शुरुआती",
    diffEasy: "आसान",
    diffMedium: "मध्यम",
    popularTitle: "सबसे लोकप्रिय उपाय",
    popularDesc: "हजारों समुदाय सदस्यों द्वारा आजमाया और पसंद किया गया",
    seasonalTitle: "मौसमी उपाय",
    seasonalDesc: "हर भारतीय मौसम के लिए विशेष फॉर्मूलेशन",
    seasons: ["सर्दियों के लिए", "गर्मियों के लिए", "मानसून देखभाल"],
    tipsTitle: "प्रो टिप्स और ट्रिक्स",
    tipsDesc: "DIY सौंदर्य परिणामों को अधिकतम करने के लिए विशेषज्ञ सलाह",
    tips: [
      {
        icon: "🧪",
        title: "पहले पैच टेस्ट करें",
        desc: "कलाई पर या कान के पीछे थोड़ी मात्रा लगाएं और चेहरे पर लगाने से पहले 24 घंटे प्रतीक्षा करें, विशेषकर नींबू, दालचीनी या हल्दी के साथ।",
      },
      {
        icon: "🕐",
        title: "रात को लगाना सबसे अच्छा",
        desc: "मास्क और सीरम रात को लगाएं जब त्वचा मरम्मत मोड में हो। नींबू और हल्दी धूप में फोटोसेंसिटिविटी पैदा कर सकते हैं।",
      },
      {
        icon: "🌡️",
        title: "DIY उत्पाद फ्रिज में रखें",
        desc: "घरेलू उत्पादों में कोई प्रिजर्वेटिव नहीं होता। एयरटाइट कांच के कंटेनर में रखें और 3–5 दिनों में उपयोग करें।",
      },
      {
        icon: "💧",
        title: "अंदर से हाइड्रेट करें",
        desc: "रोज़ाना कम से कम 8 गिलास पानी पिएं। निर्जलित त्वचा पर कोई भी उपाय अच्छी तरह काम नहीं करता।",
      },
      {
        icon: "🔄",
        title: "नियमितता जरूरी है",
        desc: "DIY उपाय सौम्य और प्राकृतिक हैं — परिणाम दिखाने के लिए 2–4 सप्ताह की नियमित उपयोग जरूरी है।",
      },
      {
        icon: "🌿",
        title: "केवल ताजी सामग्री",
        desc: "जब भी संभव हो ताजी, जैविक सामग्री का उपयोग करें। बासी शहद, एलोवेरा या फलों के सक्रिय यौगिक नष्ट हो जाते हैं।",
      },
    ],
    ctaTitle: "अपनी सौंदर्य दिनचर्या बदलने के लिए तैयार हैं?",
    ctaDesc: `${COMMUNITY_COUNT_HI} लोगों से जुड़ें जिन्होंने प्राकृतिक सौंदर्य की शक्ति खोजी है!`,
    btnSubYT: "नए ट्यूटोरियल के लिए सब्सक्राइब करें",
    btnFollowIG: "दैनिक टिप्स के लिए फॉलो करें",
    newsTitle: "साप्ताहिक DIY टिप्स और एक्सक्लूसिव रेसिपी पाएं",
    newsDesc: "नवीनतम प्राकृतिक सौंदर्य रहस्यों के लिए हमारे न्यूज़लेटर से जुड़ें!",
    newsPlaceholder: "अपना ईमेल पता दर्ज करें",
    btnSub: "सब्सक्राइब करें",
    subDone: "🎉 आप सब्सक्राइब हो गए! धन्यवाद।",
    emailErr: "कृपया एक मान्य ईमेल पता दर्ज करें।",
    testTitle: "हमारा समुदाय क्या कहता है",
    testDesc: "पूरे India के वास्तविक लोगों के वास्तविक परिणाम",
    usedLabel: "उपयोग किया",
    disclaimerStrong: "⚠️ महत्वपूर्ण अस्वीकरण:",
    disclaimerText: "ये DIY उपाय पारंपरिक आयुर्वेदिक ज्ञान पर आधारित हैं। हमेशा",
    patchTest: "पैच टेस्ट करें",
    disclaimerEnd: "पूरे आवेदन से 24 घंटे पहले। धूप से पहले खट्टे उपाय न लगाएं। संवेदनशील त्वचा होने पर त्वचा विशेषज्ञ से परामर्श करें।",
    footerTagline: `प्राकृतिक सौंदर्य • DIY उपाय • ${COMMUNITY_COUNT_HI} समुदाय`,
    footerYT: `YouTube (${COMMUNITY_COUNT_HI})`,
    footerIG: `Instagram (${IG_COUNT})`,
    footerHome: "मुख्य वेबसाइट",
    footerCopy: "प्राकृतिक सौंदर्य प्रेमियों के लिए 💖 के साथ बनाया गया",
  },

  ur: {
    siteName: "تانیہ فیشن سکن کیئر",
    siteTagline: "DIY خوبصورتی کے نسخے",
    navHome: "ہوم",
    navCategories: "زمرے",
    navPopular: "مقبول",
    navBack: "ہوم پر واپس",
    heroBadge: "50+ آزمودہ قدرتی نسخے",
    heroTitle: "قدرتی خوبصورتی کے نسخے جو",
    heroHighlight: "واقعی کام کرتے ہیں!",
    heroDesc: `سادہ باورچی خانے کی اشیاء سے اپنی جلد اور بالوں کو بدلیں۔ مہنگی مصنوعات کی ضرورت نہیں — ${COMMUNITY_COUNT_UR} کمیونٹی سے ثابت!`,
    tags: ["100% قدرتی", "باورچی خانے کی اشیاء", "ثابت نتائج", "صفر مضر اثرات"],
    btnYT: "YouTube پر ٹیوٹوریل دیکھیں",
    btnAI: "AI چہرہ تجزیہ آزمائیں",
    stat1: "50+",
    stat1L: "DIY نسخے",
    stat2: COMMUNITY_COUNT_UR,
    stat2L: "خوش کمیونٹی",
    stat3: "95%",
    stat3L: "کامیابی کی شرح",
    stat4: "100%",
    stat4L: "قدرتی و محفوظ",
    browseTitle: "زمرے کے مطابق تلاش کریں",
    cats: [
      { id: "all", name: "تمام نسخے", count: "50+" },
      { id: "skin", name: "جلد کی دیکھ بھال", count: "20+" },
      { id: "hair", name: "بالوں کی دیکھ بھال", count: "15+" },
      { id: "acne", name: "کیل مہاسے", count: "10+" },
      { id: "anti-aging", name: "اینٹی ایجنگ", count: "8+" },
    ],
    collTitle: "مکمل DIY نسخوں کا مجموعہ",
    collDesc: "ہر نسخے کے لیے درست اجزاء، وقت اور مشکل کے ساتھ مرحلہ وار رہنمائی۔",
    remediesAvail: "نسخے دستیاب",
    ingLabel: "اجزاء",
    benLabel: "فائدہ",
    howLabel: "طریقہ",
    diffBeginner: "ابتدائی",
    diffEasy: "آسان",
    diffMedium: "درمیانی",
    popularTitle: "سب سے مقبول نسخے",
    popularDesc: "ہزاروں کمیونٹی اراکین نے آزمایا اور پسند کیا",
    seasonalTitle: "موسمی نسخے",
    seasonalDesc: "ہر ہندوستانی موسم کے لیے خصوصی فارمولیشن",
    seasons: ["سردیوں کے لیے", "گرمیوں کے لیے", "مانسون کی دیکھ بھال"],
    tipsTitle: "ماہرانہ ٹپس اور ترکیبیں",
    tipsDesc: "DIY خوبصورتی کے نتائج کو بہتر بنانے کے لیے ماہرانہ مشورہ",
    tips: [
      {
        icon: "🧪",
        title: "پہلے پیچ ٹیسٹ کریں",
        desc: "کلائی پر یا کان کے پیچھے تھوڑی مقدار لگائیں اور چہرے پر لگانے سے پہلے 24 گھنٹے انتظار کریں، خاص طور پر لیموں، دارچینی یا ہلدی کے ساتھ۔",
      },
      {
        icon: "🕐",
        title: "شام کا وقت بہترین ہے",
        desc: "رات کو ماسک اور سیرم لگائیں جب جلد مرمت کے موڈ میں ہو۔ لیموں اور ہلدی دھوپ میں حساسیت پیدا کر سکتے ہیں۔",
      },
      {
        icon: "🌡️",
        title: "DIY مصنوعات فریج میں رکھیں",
        desc: "گھریلو مصنوعات میں کوئی محافظ نہیں ہوتا۔ ہوا بند شیشے کے برتن میں رکھیں اور 3–5 دنوں میں استعمال کریں۔",
      },
      {
        icon: "💧",
        title: "اندر سے ہائیڈریٹ کریں",
        desc: "روزانہ کم از کم 8 گلاس پانی پئیں۔ پانی کی کمی والی جلد پر کوئی بھی نسخہ اچھی طرح کام نہیں کرتا۔",
      },
      {
        icon: "🔄",
        title: "مستقل مزاجی ضروری ہے",
        desc: "DIY نسخے نرم اور قدرتی ہیں — نتائج دکھانے کے لیے 2–4 ہفتے کا باقاعدہ استعمال ضروری ہے۔",
      },
      {
        icon: "🌿",
        title: "صرف تازہ اجزاء",
        desc: "جہاں ممکن ہو تازہ، نامیاتی اجزاء استعمال کریں۔ باسی شہد، ایلو ویرا یا پھلوں کے فعال مرکبات ختم ہو جاتے ہیں۔",
      },
    ],
    ctaTitle: "اپنا روزانہ کا خوبصورتی کا معمول بدلنے کے لیے تیار ہیں؟",
    ctaDesc: `${COMMUNITY_COUNT_UR} لوگوں سے جڑیں جنہوں نے قدرتی خوبصورتی کی طاقت دریافت کی!`,
    btnSubYT: "نئے ٹیوٹوریل کے لیے سبسکرائب کریں",
    btnFollowIG: "روزانہ ٹپس کے لیے فالو کریں",
    newsTitle: "ہفتہ وار DIY ٹپس اور خصوصی نسخے پائیں",
    newsDesc: "تازہ ترین قدرتی خوبصورتی کے رازوں کے لیے ہمارے نیوز لیٹر سے جڑیں!",
    newsPlaceholder: "اپنا ای میل پتہ درج کریں",
    btnSub: "سبسکرائب کریں",
    subDone: "🎉 آپ سبسکرائب ہو گئے! شکریہ۔",
    emailErr: "براہ کرم ایک درست ای میل پتہ درج کریں۔",
    testTitle: "ہماری کمیونٹی کیا کہتی ہے",
    testDesc: "پورے بھارت کے حقیقی لوگوں کے حقیقی نتائج",
    usedLabel: "استعمال کیا",
    disclaimerStrong: "⚠️ اہم دستبرداری:",
    disclaimerText: "یہ DIY نسخے روایتی آیورویدک علم پر مبنی ہیں۔ ہمیشہ",
    patchTest: "پیچ ٹیسٹ کریں",
    disclaimerEnd: "مکمل استعمال سے 24 گھنٹے پہلے۔ دھوپ سے پہلے ترش نسخے نہ لگائیں۔ حساس جلد کی صورت میں ماہر امراض جلد سے مشورہ کریں۔",
    footerTagline: `قدرتی خوبصورتی • DIY نسخے • ${COMMUNITY_COUNT_UR} کمیونٹی`,
    footerYT: `YouTube (${COMMUNITY_COUNT_UR})`,
    footerIG: `Instagram (${IG_COUNT})`,
    footerHome: "مرکزی ویب سائٹ",
    footerCopy: "قدرتی خوبصورتی سے محبت کرنے والوں کے لیے 💖 کے ساتھ بنایا گیا",
  },
};

const CAT_TITLES = {
  en: {
    skin: "For Glowing Skin",
    hair: "For Hair Care",
    acne: "For Acne & Pimples",
    "anti-aging": "For Anti-Aging",
  },
  hi: {
    skin: "चमकती त्वचा के लिए",
    hair: "बालों की देखभाल",
    acne: "मुहांसों के लिए",
    "anti-aging": "एंटी-एजिंग के लिए",
  },
  ur: {
    skin: "چمکتی جلد کے لیے",
    hair: "بالوں کی دیکھ بھال",
    acne: "کیل مہاسوں کے لیے",
    "anti-aging": "اینٹی ایجنگ کے لیے",
  },
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const REMEDY_CATEGORIES = [
  {
    category: "skin",
    icon: "✨",
    color: "from-amber-400 to-orange-400",
    bgColor: "bg-amber-50",
    remedies: [
      {
        name: {
          en: "Turmeric & Milk Glow Mask",
          hi: "हल्दी और दूध ग्लो मास्क",
          ur: "ہلدی اور دودھ گلو ماسک",
        },
        ingredients: {
          en: "½ tsp Turmeric + 1 tbsp Raw Milk + 1 tsp Honey",
          hi: "½ चम्मच हल्दी + 1 बड़ा चम्मच कच्चा दूध + 1 चम्मच शहद",
          ur: "½ چمچ ہلدی + 1 بڑا چمچ کچا دودھ + 1 چمچ شہد",
        },
        benefit: {
          en: "Brightens skin, fades dark spots & evens skin tone",
          hi: "त्वचा को निखारता है, दाग-धब्बों को हल्का करता है और स्किन टोन को समान बनाता है",
          ur: "جلد کو نکھارتا ہے، داغ دھبوں کو ہلکا کرتا ہے اور رنگت کو یکساں بناتا ہے",
        },
        time: {
          en: "15 mins",
          hi: "15 मिनट",
          ur: "15 منٹ",
        },
        difficulty: "Easy",
        steps: {
          en: "Mix all ingredients. Apply evenly. Leave 15 mins. Rinse with lukewarm water.",
          hi: "सभी सामग्री मिलाएं। चेहरे पर समान रूप से लगाएं। 15 मिनट तक छोड़ दें। गुनगुने पानी से धो लें।",
          ur: "تمام اجزاء ملا لیں۔ چہرے پر یکساں طور پر لگائیں۔ 15 منٹ تک لگا رہنے دیں۔ نیم گرم پانی سے دھو لیں۔",
        },
      },
      {
        name: {
          en: "Besan Ubtan Scrub",
          hi: "बेसन उबटन स्क्रब",
          ur: "بیسن اُبٹن اسکرب",
        },
        ingredients: {
          en: "2 tbsp Besan + 1 tsp Turmeric + Rose Water + Milk",
          hi: "2 बड़े चम्मच बेसन + 1 चम्मच हल्दी + गुलाब जल + दूध",
          ur: "2 بڑے چمچ بیسن + 1 چمچ ہلدی + عرقِ گلاب + دودھ",
        },
        benefit: {
          en: "Deep cleanses, removes tan & gives natural fairness",
          hi: "गहराई से सफाई करता है, टैन हटाता है और प्राकृतिक निखार देता है",
          ur: "گہری صفائی کرتا ہے، ٹین کم کرتا ہے اور قدرتی نکھار دیتا ہے",
        },
        time: {
          en: "20 mins",
          hi: "20 मिनट",
          ur: "20 منٹ",
        },
        difficulty: "Easy",
        steps: {
          en: "Make thick paste. Scrub gently in circular motion. Rinse after 10 mins.",
          hi: "गाढ़ा पेस्ट बनाएं। हल्के हाथों से गोल-गोल घुमाते हुए स्क्रब करें। 10 मिनट बाद धो लें।",
          ur: "گاڑھا پیسٹ بنائیں۔ آہستہ سے گولائی میں اسکرب کریں۔ 10 منٹ بعد دھو لیں۔",
        },
      },
      {
        name: {
          en: "Aloe Vera & Cucumber Gel",
          hi: "एलोवेरा और खीरा जेल",
          ur: "ایلوویرا اور کھیرا جیل",
        },
        ingredients: {
          en: "Fresh Aloe Gel + Grated Cucumber + 2 drops Rose Water",
          hi: "ताज़ा एलो जेल + कद्दूकस किया हुआ खीरा + 2 बूंद गुलाब जल",
          ur: "تازہ ایلو جیل + کدوکش کیا ہوا کھیرا + 2 قطرے عرقِ گلاب",
        },
        benefit: {
          en: "Soothes, hydrates & reduces redness instantly",
          hi: "त्वचा को शांत करता है, नमी देता है और लालिमा तुरंत कम करता है",
          ur: "جلد کو سکون دیتا ہے، نمی پہنچاتا ہے اور سرخی فوراً کم کرتا ہے",
        },
        time: {
          en: "10 mins",
          hi: "10 मिनट",
          ur: "10 منٹ",
        },
        difficulty: "Beginner",
        steps: {
          en: "Blend cucumber. Mix with aloe gel. Apply on face & neck. Rinse after 10 mins.",
          hi: "खीरे को पीस लें। इसे एलो जेल में मिलाएं। चेहरे और गर्दन पर लगाएं। 10 मिनट बाद धो लें।",
          ur: "کھیرے کو بلینڈ کر لیں۔ اسے ایلو جیل میں ملائیں۔ چہرے اور گردن پر لگائیں۔ 10 منٹ بعد دھو لیں۔",
        },
      },
      {
        name: {
          en: "Papaya Enzyme Brightening Mask",
          hi: "पपीता एंज़ाइम ब्राइटनिंग मास्क",
          ur: "پپیتا انزائم برائٹننگ ماسک",
        },
        ingredients: {
          en: "2 tbsp Ripe Papaya + 1 tsp Honey + ½ tsp Lemon Juice",
          hi: "2 बड़े चम्मच पका पपीता + 1 चम्मच शहद + ½ चम्मच नींबू रस",
          ur: "2 بڑے چمچ پکا ہوا پپیتا + 1 چمچ شہد + ½ چمچ لیموں کا رس",
        },
        benefit: {
          en: "Exfoliates dead cells, lightens pigmentation & adds glow",
          hi: "मृत कोशिकाएं हटाता है, पिग्मेंटेशन हल्का करता है और ग्लो बढ़ाता है",
          ur: "مردہ خلیات ہٹاتا ہے، پگمنٹیشن کم کرتا ہے اور چمک بڑھاتا ہے",
        },
        time: {
          en: "12 mins",
          hi: "12 मिनट",
          ur: "12 منٹ",
        },
        difficulty: "Easy",
        steps: {
          en: "Mash ripe papaya. Add honey and lemon. Apply for 12 mins. Rinse thoroughly.",
          hi: "पके पपीते को मैश करें। इसमें शहद और नींबू मिलाएं। 12 मिनट तक लगाकर रखें। अच्छी तरह धो लें।",
          ur: "پکے ہوئے پپیتے کو میش کریں۔ اس میں شہد اور لیموں ملائیں۔ 12 منٹ تک لگائیں۔ اچھی طرح دھو لیں۔",
        },
      },
      {
        name: {
          en: "Rose Water Toner Mist",
          hi: "गुलाब जल टोनर मिस्ट",
          ur: "عرقِ گلاب ٹونر مسٹ",
        },
        ingredients: {
          en: "Pure Rose Water + 2 drops Glycerin + 1 drop Vitamin E Oil",
          hi: "शुद्ध गुलाब जल + 2 बूंद ग्लिसरीन + 1 बूंद विटामिन E ऑयल",
          ur: "خالص عرقِ گلاب + 2 قطرے گلیسرین + 1 قطرہ وٹامن ای آئل",
        },
        benefit: {
          en: "Balances pH, tightens pores & keeps skin dewy all day",
          hi: "pH संतुलित करता है, रोमछिद्र कसता है और त्वचा को दिनभर ताज़ा रखता है",
          ur: "pH متوازن کرتا ہے، مسام کو ٹائٹ کرتا ہے اور جلد کو دن بھر تروتازہ رکھتا ہے",
        },
        time: {
          en: "2 mins",
          hi: "2 मिनट",
          ur: "2 منٹ",
        },
        difficulty: "Beginner",
        steps: {
          en: "Mix in spray bottle. Spritz on clean face morning and evening.",
          hi: "सभी सामग्री स्प्रे बोतल में मिलाएं। सुबह और शाम साफ चेहरे पर स्प्रे करें।",
          ur: "سب اجزاء اسپرے بوتل میں ملا لیں۔ صبح اور شام صاف چہرے پر اسپرے کریں۔",
        },
      },
    ],
  },
  {
    category: "hair",
    icon: "🌿",
    color: "from-green-400 to-emerald-400",
    bgColor: "bg-green-50",
    remedies: [
      {
        name: {
          en: "Onion Juice Hair Growth Serum",
          hi: "प्याज़ रस हेयर ग्रोथ सीरम",
          ur: "پیاز کے رس کا ہیئر گروتھ سیرم",
        },
        ingredients: {
          en: "3 tbsp Onion Juice + 1 tbsp Castor Oil + 5 drops Rosemary Oil",
          hi: "3 बड़े चम्मच प्याज़ का रस + 1 बड़ा चम्मच कैस्टर ऑयल + 5 बूंद रोज़मेरी ऑयल",
          ur: "3 بڑے چمچ پیاز کا رس + 1 بڑا چمچ کیسٹر آئل + 5 قطرے روزمیری آئل",
        },
        benefit: {
          en: "Stimulates hair follicles, reduces hair fall by 80%",
          hi: "बालों की जड़ों को सक्रिय करता है और हेयर फॉल कम करने में मदद करता है",
          ur: "بالوں کی جڑوں کو متحرک کرتا ہے اور بال جھڑنا کم کرنے میں مدد دیتا ہے",
        },
        time: { en: "45 mins", hi: "45 मिनट", ur: "45 منٹ" },
        difficulty: "Medium",
        steps: {
          en: "Blend onion, strain juice. Mix with oils. Massage into scalp. Leave 45 mins. Wash with mild shampoo.",
          hi: "प्याज़ को पीसकर रस छान लें। इसमें तेल मिलाएं। स्कैल्प पर मसाज करें। 45 मिनट बाद माइल्ड शैम्पू से धो लें।",
          ur: "پیاز کو بلینڈ کر کے رس چھان لیں۔ اس میں تیل ملائیں۔ سر کی جلد پر مساج کریں۔ 45 منٹ بعد ہلکے شیمپو سے دھو لیں۔",
        },
      },
      {
        name: {
          en: "Egg & Curd Protein Mask",
          hi: "अंडा और दही प्रोटीन मास्क",
          ur: "انڈا اور دہی پروٹین ماسک",
        },
        ingredients: {
          en: "1 Egg + 3 tbsp Curd + 1 tbsp Coconut Oil + 1 tsp Honey",
          hi: "1 अंडा + 3 बड़े चम्मच दही + 1 बड़ा चम्मच नारियल तेल + 1 चम्मच शहद",
          ur: "1 انڈا + 3 بڑے چمچ دہی + 1 بڑا چمچ ناریل کا تیل + 1 چمچ شہد",
        },
        benefit: {
          en: "Repairs damaged hair, adds shine & reduces breakage",
          hi: "डैमेज बालों को रिपेयर करता है, चमक बढ़ाता है और टूटना कम करता है",
          ur: "خراب بالوں کی مرمت کرتا ہے، چمک بڑھاتا ہے اور ٹوٹنا کم کرتا ہے",
        },
        time: { en: "30 mins", hi: "30 मिनट", ur: "30 منٹ" },
        difficulty: "Easy",
        steps: {
          en: "Whisk egg with curd and oil. Apply root to tip. Cover with shower cap. Rinse with cool water.",
          hi: "अंडे को दही और तेल के साथ फेंट लें। जड़ों से सिरों तक लगाएं। शावर कैप पहनें। ठंडे पानी से धो लें।",
          ur: "انڈے کو دہی اور تیل کے ساتھ پھینٹ لیں۔ جڑوں سے سروں تک لگائیں۔ شاور کیپ پہنیں۔ ٹھنڈے پانی سے دھو لیں۔",
        },
      },
      {
        name: {
          en: "Neem Anti-Dandruff Rinse",
          hi: "नीम एंटी-डैंड्रफ रिंस",
          ur: "نیم اینٹی ڈینڈرف رِنس",
        },
        ingredients: {
          en: "20 Neem Leaves + 500ml Water + 5 drops Tea Tree Oil",
          hi: "20 नीम पत्ते + 500ml पानी + 5 बूंद टी ट्री ऑयल",
          ur: "20 نیم کے پتے + 500ml پانی + 5 قطرے ٹی ٹری آئل",
        },
        benefit: {
          en: "Kills dandruff-causing fungus, soothes itchy scalp",
          hi: "डैंड्रफ पैदा करने वाले फंगस को कम करता है और खुजली वाली स्कैल्प को शांत करता है",
          ur: "ڈینڈرف پیدا کرنے والے فنگس کو کم کرتا ہے اور خارش والی کھوپڑی کو سکون دیتا ہے",
        },
        time: { en: "30 mins", hi: "30 मिनट", ur: "30 منٹ" },
        difficulty: "Medium",
        steps: {
          en: "Boil neem leaves in water for 15 mins. Cool completely. Add tea tree oil. Use as final rinse.",
          hi: "नीम के पत्तों को 15 मिनट पानी में उबालें। पूरी तरह ठंडा करें। टी ट्री ऑयल मिलाएं। अंतिम रिंस के रूप में उपयोग करें।",
          ur: "نیم کے پتوں کو 15 منٹ پانی میں ابالیں۔ مکمل ٹھنڈا کریں۔ ٹی ٹری آئل ملائیں۔ آخر میں رِنس کے طور پر استعمال کریں۔",
        },
      },
      {
        name: {
          en: "Fenugreek Seed Conditioning Pack",
          hi: "मेथी सीड कंडीशनिंग पैक",
          ur: "میتھی دانہ کنڈیشننگ پیک",
        },
        ingredients: {
          en: "3 tbsp Fenugreek (soaked overnight) + 2 tbsp Curd + 1 tsp Lemon",
          hi: "3 बड़े चम्मच रातभर भीगी मेथी + 2 बड़े चम्मच दही + 1 चम्मच नींबू",
          ur: "3 بڑے چمچ رات بھر بھگوئی ہوئی میتھی + 2 بڑے چمچ دہی + 1 چمچ لیموں",
        },
        benefit: {
          en: "Deeply conditions, controls frizz & adds volume",
          hi: "गहराई से कंडीशन करता है, फ्रिज कम करता है और वॉल्यूम बढ़ाता है",
          ur: "گہرا کنڈیشن کرتا ہے، فریز کم کرتا ہے اور حجم بڑھاتا ہے",
        },
        time: { en: "40 mins", hi: "40 मिनट", ur: "40 منٹ" },
        difficulty: "Medium",
        steps: {
          en: "Grind soaked seeds to paste. Mix with curd and lemon. Apply on hair. Leave 40 mins. Rinse well.",
          hi: "भीगे हुए दानों को पीसकर पेस्ट बनाएं। दही और नींबू मिलाएं। बालों पर लगाएं। 40 मिनट बाद अच्छी तरह धो लें।",
          ur: "بھیگے ہوئے دانوں کو پیس کر پیسٹ بنائیں۔ دہی اور لیموں ملائیں۔ بالوں پر لگائیں۔ 40 منٹ بعد اچھی طرح دھو لیں۔",
        },
      },
      {
        name: {
          en: "Rice Water Strengthening Rinse",
          hi: "राइस वॉटर स्ट्रेंथनिंग रिंस",
          ur: "رائس واٹر مضبوطی دینے والا رِنس",
        },
        ingredients: {
          en: "Fermented Rice Water (24 hrs) + 2 drops Lavender Oil",
          hi: "24 घंटे फर्मेंटेड चावल का पानी + 2 बूंद लैवेंडर ऑयल",
          ur: "24 گھنٹے خمیر شدہ چاول کا پانی + 2 قطرے لیونڈر آئل",
        },
        benefit: {
          en: "Strengthens hair shaft, promotes growth & adds shine",
          hi: "बालों को मजबूत करता है, ग्रोथ बढ़ाता है और चमक देता है",
          ur: "بالوں کو مضبوط کرتا ہے، بڑھوتری میں مدد دیتا ہے اور چمک دیتا ہے",
        },
        time: { en: "20 mins", hi: "20 मिनट", ur: "20 منٹ" },
        difficulty: "Beginner",
        steps: {
          en: "Soak rice in water 24 hrs. Strain. Add lavender oil. Pour on hair after shampoo. Leave 20 mins.",
          hi: "चावल को 24 घंटे पानी में भिगोएँ। छान लें। लैवेंडर ऑयल मिलाएं। शैम्पू के बाद बालों पर डालें। 20 मिनट तक छोड़ दें।",
          ur: "چاول کو 24 گھنٹے پانی میں بھگوئیں۔ چھان لیں۔ لیونڈر آئل ملائیں۔ شیمپو کے بعد بالوں پر ڈالیں۔ 20 منٹ تک لگا رہنے دیں۔",
        },
      },
    ],
  },
  {
    category: "acne",
    icon: "🌸",
    color: "from-pink-400 to-rose-400",
    bgColor: "bg-pink-50",
    remedies: [
      {
        name: {
          en: "Multani Mitti Deep Pore Mask",
          hi: "मुल्तानी मिट्टी डीप पोर्स मास्क",
          ur: "ملتانی مٹی ڈیپ پور ماسک",
        },
        ingredients: {
          en: "2 tbsp Multani Mitti + Rose Water + 1 tsp Neem Powder",
          hi: "2 बड़े चम्मच मुल्तानी मिट्टी + गुलाब जल + 1 चम्मच नीम पाउडर",
          ur: "2 بڑے چمچ ملتانی مٹی + عرقِ گلاب + 1 چمچ نیم پاؤڈر",
        },
        benefit: {
          en: "Absorbs excess oil, unclogs pores & prevents breakouts",
          hi: "अतिरिक्त तेल सोखता है, पोर्स साफ करता है और ब्रेकआउट कम करने में मदद करता है",
          ur: "اضافی تیل جذب کرتا ہے، مسام صاف کرتا ہے اور بریک آؤٹ کم کرنے میں مدد دیتا ہے",
        },
        time: { en: "20 mins", hi: "20 मिनट", ur: "20 منٹ" },
        difficulty: "Easy",
        steps: {
          en: "Mix to smooth paste with rose water. Apply on oily zones. Dry 15–20 mins. Rinse with cold water.",
          hi: "गुलाब जल मिलाकर स्मूद पेस्ट बनाएं। ऑयली हिस्सों पर लगाएं। 15–20 मिनट सूखने दें। ठंडे पानी से धो लें।",
          ur: "عرقِ گلاب کے ساتھ ہموار پیسٹ بنائیں۔ تیل والی جگہوں پر لگائیں۔ 15–20 منٹ خشک ہونے دیں۔ ٹھنڈے پانی سے دھو لیں۔",
        },
      },
      {
        name: {
          en: "Tea Tree Spot Treatment",
          hi: "टी ट्री स्पॉट ट्रीटमेंट",
          ur: "ٹی ٹری اسپاٹ ٹریٹمنٹ",
        },
        ingredients: {
          en: "1 drop Tea Tree Oil + 3 drops Aloe Vera Gel",
          hi: "1 बूंद टी ट्री ऑयल + 3 बूंद एलोवेरा जेल",
          ur: "1 قطرہ ٹی ٹری آئل + 3 قطرے ایلوویرا جیل",
        },
        benefit: {
          en: "Kills acne bacteria, reduces redness & swelling overnight",
          hi: "मुहांसे पैदा करने वाले बैक्टीरिया को कम करता है और रातभर में लालिमा व सूजन घटाता है",
          ur: "مہاسے پیدا کرنے والے بیکٹیریا کو کم کرتا ہے اور رات بھر میں سرخی اور سوجن گھٹاتا ہے",
        },
        time: { en: "Overnight", hi: "रातभर", ur: "رات بھر" },
        difficulty: "Beginner",
        steps: {
          en: "Dilute tea tree in aloe gel. Dab directly on pimple using cotton bud. Leave overnight.",
          hi: "टी ट्री ऑयल को एलो जेल में मिलाकर पतला करें। कॉटन बड से सीधे पिंपल पर लगाएं। रातभर छोड़ दें।",
          ur: "ٹی ٹری آئل کو ایلو جیل میں ملا کر ہلکا کریں۔ کاٹن بڈ سے براہِ راست دانے پر لگائیں۔ رات بھر لگا رہنے دیں۔",
        },
      },
      {
        name: {
          en: "Green Tea & Honey Calming Mask",
          hi: "ग्रीन टी और शहद शांतिदायक मास्क",
          ur: "گرین ٹی اور شہد سکون بخش ماسک",
        },
        ingredients: {
          en: "2 tbsp Brewed Green Tea + 1 tbsp Honey + 1 tsp Aloe Vera",
          hi: "2 बड़े चम्मच बनी हुई ग्रीन टी + 1 बड़ा चम्मच शहद + 1 चम्मच एलोवेरा",
          ur: "2 بڑے چمچ بنی ہوئی گرین ٹی + 1 بڑا چمچ شہد + 1 چمچ ایلوویرا",
        },
        benefit: {
          en: "Reduces inflammation, fights bacteria & soothes irritated skin",
          hi: "इन्फ्लेमेशन कम करता है, बैक्टीरिया से लड़ता है और त्वचा को शांत करता है",
          ur: "سوزش کم کرتا ہے، بیکٹیریا سے لڑتا ہے اور جلد کو سکون دیتا ہے",
        },
        time: { en: "15 mins", hi: "15 मिनट", ur: "15 منٹ" },
        difficulty: "Easy",
        steps: {
          en: "Brew green tea, cool completely. Mix with honey and aloe. Apply evenly. Rinse after 15 mins.",
          hi: "ग्रीन टी बनाकर पूरी तरह ठंडी करें। इसमें शहद और एलो मिलाएं। समान रूप से लगाएं। 15 मिनट बाद धो लें।",
          ur: "گرین ٹی بنا کر مکمل ٹھنڈی کریں۔ اس میں شہد اور ایلو ملائیں۔ یکساں طور پر لگائیں۔ 15 منٹ بعد دھو لیں۔",
        },
      },
      {
        name: {
          en: "Apple Cider Vinegar Toner",
          hi: "एप्पल साइडर विनेगर टोनर",
          ur: "ایپل سائڈر ونیگر ٹونر",
        },
        ingredients: {
          en: "1 part ACV + 3 parts Water + 1 drop Tea Tree Oil",
          hi: "1 भाग ACV + 3 भाग पानी + 1 बूंद टी ट्री ऑयल",
          ur: "1 حصہ ACV + 3 حصے پانی + 1 قطرہ ٹی ٹری آئل",
        },
        benefit: {
          en: "Balances skin pH, minimizes pores & fades acne marks",
          hi: "स्किन pH संतुलित करता है, पोर्स छोटे दिखाता है और एक्ने मार्क्स हल्के करता है",
          ur: "جلد کا pH متوازن کرتا ہے، مسام کم نمایاں کرتا ہے اور مہاسوں کے نشان ہلکے کرتا ہے",
        },
        time: { en: "3 mins", hi: "3 मिनट", ur: "3 منٹ" },
        difficulty: "Beginner",
        steps: {
          en: "Dilute ACV well. Soak cotton pad. Swipe on clean skin. Do not rinse. Use only at night.",
          hi: "ACV को अच्छी तरह पतला करें। कॉटन पैड भिगोएँ। साफ त्वचा पर लगाएं। न धोएँ। केवल रात में उपयोग करें।",
          ur: "ACV کو اچھی طرح پتلا کریں۔ کاٹن پیڈ بھگوئیں۔ صاف جلد پر لگائیں۔ نہ دھوئیں۔ صرف رات میں استعمال کریں۔",
        },
      },
      {
        name: {
          en: "Cinnamon & Honey Antibacterial Mask",
          hi: "दालचीनी और शहद एंटीबैक्टीरियल मास्क",
          ur: "دارچینی اور شہد اینٹی بیکٹیریل ماسک",
        },
        ingredients: {
          en: "2 tsp Raw Honey + 1 tsp Cinnamon Powder",
          hi: "2 चम्मच कच्चा शहद + 1 चम्मच दालचीनी पाउडर",
          ur: "2 چمچ کچا شہد + 1 چمچ دارچینی پاؤڈر",
        },
        benefit: {
          en: "Powerful antibacterial combo that kills P. acnes bacteria",
          hi: "यह शक्तिशाली एंटीबैक्टीरियल मिश्रण एक्ने बैक्टीरिया को कम करने में मदद करता है",
          ur: "یہ طاقتور اینٹی بیکٹیریل مرکب مہاسوں کے بیکٹیریا کو کم کرنے میں مدد دیتا ہے",
        },
        time: { en: "10 mins", hi: "10 मिनट", ur: "10 منٹ" },
        difficulty: "Beginner",
        steps: {
          en: "Mix to thick paste. Apply thin layer on affected areas. Leave 10 mins. Rinse thoroughly with warm water.",
          hi: "गाढ़ा पेस्ट बनाएं। प्रभावित हिस्सों पर पतली परत लगाएं। 10 मिनट छोड़ दें। गुनगुने पानी से अच्छी तरह धो लें।",
          ur: "گاڑھا پیسٹ بنائیں۔ متاثرہ حصوں پر پتلی تہہ لگائیں۔ 10 منٹ لگا رہنے دیں۔ نیم گرم پانی سے اچھی طرح دھو لیں۔",
        },
      },
    ],
  },
  {
    category: "anti-aging",
    icon: "💫",
    color: "from-purple-400 to-indigo-400",
    bgColor: "bg-purple-50",
    remedies: [
      {
        name: {
          en: "Vitamin E & Rose Hip Night Serum",
          hi: "विटामिन E और रोज़ हिप नाइट सीरम",
          ur: "وٹامن ای اور روز ہپ نائٹ سیرم",
        },
        ingredients: {
          en: "2 Vitamin E Capsules + 1 tsp Rose Hip Oil + 3 drops Lavender Oil",
          hi: "2 विटामिन E कैप्सूल + 1 चम्मच रोज़ हिप ऑयल + 3 बूंद लैवेंडर ऑयल",
          ur: "2 وٹامن ای کیپسول + 1 چمچ روز ہپ آئل + 3 قطرے لیونڈر آئل",
        },
        benefit: {
          en: "Reduces fine lines, repairs skin cells & fades dark spots",
          hi: "फाइन लाइन्स कम करता है, स्किन रिपेयर में मदद करता है और दाग हल्के करता है",
          ur: "باریک لکیریں کم کرتا ہے، جلد کی مرمت میں مدد دیتا ہے اور داغ ہلکے کرتا ہے",
        },
        time: { en: "Overnight", hi: "रातभर", ur: "رات بھر" },
        difficulty: "Easy",
        steps: {
          en: "Pierce capsules, extract oil. Mix with rosehip and lavender. Massage 3–4 drops on face at night.",
          hi: "कैप्सूल छेदकर तेल निकालें। रोज़ हिप और लैवेंडर ऑयल मिलाएं। रात को चेहरे पर 3–4 बूंदों से मसाज करें।",
          ur: "کیپسول میں سوراخ کر کے تیل نکالیں۔ روز ہپ اور لیونڈر آئل کے ساتھ ملائیں۔ رات کو چہرے پر 3–4 قطروں سے مساج کریں۔",
        },
      },
      {
        name: {
          en: "Egg White Firming Mask",
          hi: "एग व्हाइट फर्मिंग मास्क",
          ur: "ایگ وائٹ فرمِنگ ماسک",
        },
        ingredients: {
          en: "1 Egg White + 1 tsp Lemon Juice + 1 tsp Honey",
          hi: "1 अंडे का सफेद भाग + 1 चम्मच नींबू रस + 1 चम्मच शहद",
          ur: "1 انڈے کی سفیدی + 1 چمچ لیموں کا رس + 1 چمچ شہد",
        },
        benefit: {
          en: "Firms sagging skin, tightens pores & lifts fine lines",
          hi: "ढीली त्वचा को टाइट करता है, पोर्स कसता है और फाइन लाइन्स कम दिखाता है",
          ur: "ڈھیلی جلد کو ٹائٹ کرتا ہے، مسام سکیڑتا ہے اور باریک لکیریں کم نمایاں کرتا ہے",
        },
        time: { en: "20 mins", hi: "20 मिनट", ur: "20 منٹ" },
        difficulty: "Medium",
        steps: {
          en: "Whisk egg white until frothy. Add lemon and honey. Apply in upward strokes. Rinse after tightening.",
          hi: "अंडे की सफेदी को झागदार होने तक फेंटें। नींबू और शहद मिलाएं। ऊपर की दिशा में लगाएं। सूखने के बाद धो लें।",
          ur: "انڈے کی سفیدی کو جھاگ آنے تک پھینٹیں۔ لیموں اور شہد ملائیں۔ اوپر کی سمت میں لگائیں۔ ٹائٹ ہونے کے بعد دھو لیں۔",
        },
      },
      {
        name: {
          en: "Avocado Anti-Wrinkle Mask",
          hi: "एवोकाडो एंटी-रिंकल मास्क",
          ur: "ایووکاڈو اینٹی رِنکل ماسک",
        },
        ingredients: {
          en: "½ Ripe Avocado + 1 tsp Olive Oil + 1 tsp Honey + Vitamin E",
          hi: "½ पका एवोकाडो + 1 चम्मच ऑलिव ऑयल + 1 चम्मच शहद + विटामिन E",
          ur: "½ پکا ہوا ایووکاڈو + 1 چمچ زیتون کا تیل + 1 چمچ شہد + وٹامن ای",
        },
        benefit: {
          en: "Intense nourishment, plumps skin & reduces wrinkle depth",
          hi: "गहरा पोषण देता है, त्वचा को भरा हुआ दिखाता है और झुर्रियों की गहराई कम करता है",
          ur: "گہری غذائیت دیتا ہے، جلد کو بھرا ہوا دکھاتا ہے اور جھریوں کی گہرائی کم کرتا ہے",
        },
        time: { en: "25 mins", hi: "25 मिनट", ur: "25 منٹ" },
        difficulty: "Easy",
        steps: {
          en: "Mash avocado thoroughly. Mix all ingredients to smooth paste. Apply generously. Rinse after 25 mins.",
          hi: "एवोकाडो को अच्छी तरह मैश करें। सभी सामग्री मिलाकर स्मूद पेस्ट बनाएं। उदार मात्रा में लगाएं। 25 मिनट बाद धो लें।",
          ur: "ایووکاڈو کو اچھی طرح میش کریں۔ تمام اجزاء ملا کر ہموار پیسٹ بنائیں۔ اچھی مقدار میں لگائیں۔ 25 منٹ بعد دھو لیں۔",
        },
      },
      {
        name: {
          en: "Green Tea Ice Cube Massage",
          hi: "ग्रीन टी आइस क्यूब मसाज",
          ur: "گرین ٹی آئس کیوب مساج",
        },
        ingredients: {
          en: "Brewed Green Tea (frozen) + 1 drop Rosehip Oil",
          hi: "जमी हुई ग्रीन टी + 1 बूंद रोज़हिप ऑयल",
          ur: "جمی ہوئی گرین ٹی + 1 قطرہ روزہپ آئل",
        },
        benefit: {
          en: "Boosts circulation, reduces puffiness & tightens skin",
          hi: "ब्लड सर्कुलेशन बढ़ाता है, सूजन कम करता है और त्वचा टाइट करता है",
          ur: "خون کی روانی بہتر کرتا ہے، سوجن کم کرتا ہے اور جلد کو ٹائٹ کرتا ہے",
        },
        time: { en: "5 mins", hi: "5 मिनट", ur: "5 منٹ" },
        difficulty: "Beginner",
        steps: {
          en: "Freeze strong green tea in ice tray. Wrap cube in cloth. Massage face in circular motion for 5 mins.",
          hi: "मज़बूत ग्रीन टी को आइस ट्रे में जमा लें। बर्फ के टुकड़े को कपड़े में लपेटें। 5 मिनट तक गोलाई में चेहरे पर मसाज करें।",
          ur: "گاڑھی گرین ٹی کو آئس ٹرے میں جما لیں۔ برف کے ٹکڑے کو کپڑے میں لپیٹیں۔ 5 منٹ تک گولائی میں چہرے پر مساج کریں۔",
        },
      },
    ],
  },
];

const POPULAR_REMEDIES = [
  { name: "Turmeric Golden Face Mask", rating: "4.9", users: "52K+", icon: "🌟", category: "Skin Brightening", time: "15 mins", ingredients: "Turmeric + Raw Milk + Honey", benefit: "Visible glow in just 2–3 uses" },
  { name: "Onion Juice Hair Serum", rating: "4.8", users: "41K+", icon: "💫", category: "Hair Regrowth", time: "45 mins", ingredients: "Onion Juice + Castor Oil + Rosemary", benefit: "Reduces hair fall, visible growth in 4 weeks" },
  { name: "Fermented Rice Water Rinse", rating: "4.8", users: "38K+", icon: "✨", category: "Hair Strengthening", time: "20 mins", ingredients: "Rice Water (fermented 24 hrs)", benefit: "Longer, stronger & shinier hair" },
  { name: "Multani Mitti Oil Control", rating: "4.7", users: "44K+", icon: "🧖", category: "Acne & Oily Skin", time: "20 mins", ingredients: "Multani Mitti + Rose Water + Neem", benefit: "Controls oil for 8+ hours" },
  { name: "Besan Ubtan Full Body Pack", rating: "4.7", users: "35K+", icon: "🌿", category: "Tan Removal", time: "25 mins", ingredients: "Besan + Turmeric + Curd + Milk", benefit: "1 shade lighter in 2 weeks" },
  { name: "Egg White Pore Tightener", rating: "4.6", users: "29K+", icon: "🥚", category: "Anti-Aging", time: "20 mins", ingredients: "Egg White + Lemon + Honey", benefit: "Visibly tighter skin in 1 use" },
];

const SEASONAL_REMEDIES = [
  {
    seasonIdx: 0,
    icon: "❄️",
    color: "from-blue-400 to-cyan-400",
    bg: "bg-blue-50",
    remedies: [
      { name: "Dry Skin Rescue Mask", ingredients: "½ Avocado + 1 tbsp Honey + 1 tbsp Oats + few drops Almond Oil", benefit: "Restores moisture barrier, relieves tightness & flaking", time: "20 mins" },
      { name: "Chapped Lips Overnight Balm", ingredients: "1 tsp Coconut Oil + ½ tsp Shea Butter + 1 drop Peppermint Oil", benefit: "Heals cracked lips overnight, keeps soft all day", time: "Overnight" },
      { name: "Deep Nourishing Body Butter", ingredients: "Coconut Oil + Shea Butter + 5 drops Lavender Oil", benefit: "Locks in moisture for 24 hrs, prevents rough patches", time: "3 mins" },
    ],
  },
  {
    seasonIdx: 1,
    icon: "☀️",
    color: "from-yellow-400 to-orange-400",
    bg: "bg-yellow-50",
    remedies: [
      { name: "Cucumber Aloe Cooling Mask", ingredients: "½ Cucumber (grated) + 2 tbsp Aloe Vera + 1 tsp Rose Water", benefit: "Instantly cools sunburnt skin, reduces redness & tan", time: "15 mins" },
      { name: "DIY Tan Removal Scrub", ingredients: "2 tbsp Besan + 1 tsp Turmeric + Lemon Juice + Curd", benefit: "Removes summer tan, evens out skin tone", time: "20 mins" },
      { name: "Tomato Pore Minimizer", ingredients: "1 Tomato (pulp) + 1 tsp Honey + ½ tsp Lemon Juice", benefit: "Shrinks enlarged pores, controls oil & cools skin", time: "10 mins" },
    ],
  },
  {
    seasonIdx: 2,
    icon: "🌧️",
    color: "from-teal-400 to-green-400",
    bg: "bg-teal-50",
    remedies: [
      { name: "Anti-Fungal Neem Face Wash", ingredients: "10 Neem Leaves + 1 tsp Turmeric + 1 tbsp Honey", benefit: "Prevents fungal breakouts common in humid weather", time: "5 mins" },
      { name: "Clay & Charcoal Detox Mask", ingredients: "1 tsp Activated Charcoal + 1 tbsp Kaolin Clay + Rose Water", benefit: "Draws out pollutants & excess humidity from pores", time: "15 mins" },
    ],
  },
];

const TESTIMONIALS = [
  { name: "Priya Sharma", location: "Mumbai, India", rating: 5, text: "The turmeric glow mask completely transformed my skin in just 2 weeks! My pigmentation faded and I get compliments every day now.", remedy: "Turmeric & Milk Glow Mask" },
  { name: "Ananya Reddy", location: "Hyderabad, India", rating: 5, text: "I was about to spend ₹2000 on a hair serum. Tried the onion juice recipe instead — my hair fall reduced by 70% in one month. Unbelievable!", remedy: "Onion Juice Hair Growth Serum" },
  { name: "Sneha Kulkarni", location: "Pune, India", rating: 4, text: "The Multani Mitti mask is my weekly must-do. My oily skin stays matte for 8+ hours and the acne marks are fading so fast!", remedy: "Multani Mitti Deep Pore Mask" },
  { name: "Fatima Khan", location: "Delhi, India", rating: 5, text: "Rice water rinse gave my hair the shine I'd been chasing for years. I've been recommending this to everyone in my family!", remedy: "Rice Water Strengthening Rinse" },
  { name: "Divya Nair", location: "Kerala, India", rating: 4, text: "The besan ubtan removed my summer tan in 10 days. Absolutely natural, zero side effects and I saved so much money!", remedy: "Besan Ubtan Scrub" },
  { name: "Meera Joshi", location: "Bangalore, India", rating: 5, text: "Egg white mask is my Friday ritual now. My pores are visibly smaller and my skin looks firmer and lifted — at 35!", remedy: "Egg White Firming Mask" },
];

// ─── Components ───────────────────────────────────────────────────────────────
function RemediesHeader() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="w-full bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden bg-white shadow-sm flex items-center justify-center">
              <img
                src="/applogo.png"
                alt="Tanya Fashion Skincare Logo"
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  if (e.target.nextSibling) e.target.nextSibling.style.display = "block";
                }}
              />
              <span className="text-pink-600 font-bold text-xl" style={{ display: "none" }}>T</span>
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                {t.siteName}
              </h1>
              <p className="text-xs text-gray-500 hidden sm:block">{t.siteTagline}</p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-4">
            <Link to="/" className="text-gray-600 hover:text-pink-600 transition-colors font-medium">{t.navHome}</Link>
            <a href="#categories" className="text-gray-600 hover:text-pink-600 transition-colors font-medium">{t.navCategories}</a>
            <a href="#popular" className="text-gray-600 hover:text-pink-600 transition-colors font-medium">{t.navPopular}</a>
            <LanguageSwitcher />
            <Link to="/" className="btn-premium px-4 py-2 rounded-lg font-semibold text-sm">{t.navBack}</Link>
          </nav>

          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-2" aria-label="Toggle menu">
              <div className="space-y-1">
                <span className="block w-6 h-0.5 bg-gray-800"></span>
                <span className="block w-6 h-0.5 bg-gray-800"></span>
                <span className="block w-6 h-0.5 bg-gray-800"></span>
              </div>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              <Link to="/" className="py-2 text-gray-600 hover:text-pink-600 font-medium">{t.navHome}</Link>
              <a href="#categories" className="py-2 text-gray-600 hover:text-pink-600 font-medium" onClick={() => setMenuOpen(false)}>{t.navCategories}</a>
              <a href="#popular" className="py-2 text-gray-600 hover:text-pink-600 font-medium" onClick={() => setMenuOpen(false)}>{t.navPopular}</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function HeroSection() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  return (
    <section className="relative bg-gradient-to-br from-rose-100 via-pink-50 to-amber-50 py-16 md:py-20 overflow-hidden" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,207,232,0.3),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(254,215,170,0.2),transparent_50%)]" />
      <div className="relative max-w-6xl mx-auto px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full border border-pink-200 mb-6" data-aos="fade-down">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-sm font-medium text-gray-700">{t.heroBadge}</span>
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }} data-aos="fade-up">
          {t.heroTitle}{" "}
          <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600 bg-clip-text text-transparent">
            {t.heroHighlight}
          </span>
        </h1>

        <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed mb-8" data-aos="fade-up" data-aos-delay="100">
          {t.heroDesc}
        </p>

        <div className="flex items-center justify-center gap-6 flex-wrap mb-8" data-aos="fade-up" data-aos-delay="150">
          {t.tags.map((tag) => (
            <div key={tag} className="flex items-center gap-2 text-gray-700">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="font-medium text-sm">{tag}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-aos="fade-up" data-aos-delay="200">
          <a href={YT_URL} target="_blank" rel="noreferrer" className="btn-premium px-8 py-4 rounded-xl text-lg font-semibold">
            {t.btnYT}
          </a>
          <Link to="/" className="px-8 py-4 bg-white rounded-xl text-lg font-semibold border-2 border-pink-200 hover:bg-pink-50 transition-all duration-300" style={{ color: "#c2185b" }}>
            {t.btnAI}
          </Link>
        </div>
      </div>
    </section>
  );
}

function QuickStatsSection() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  const stats = [
    { value: t.stat1, label: t.stat1L, color: "text-pink-600" },
    { value: t.stat2, label: t.stat2L, color: "text-green-600" },
    { value: t.stat3, label: t.stat3L, color: "text-blue-600" },
    { value: t.stat4, label: t.stat4L, color: "text-purple-600" },
  ];

  return (
    <section className="py-12 bg-white border-b border-gray-100" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center" data-aos="fade-up">
              <div className={`text-3xl md:text-4xl font-bold ${s.color} mb-2`} style={{ fontFamily: ctx.headingFont }}>
                {s.value}
              </div>
              <div className="text-gray-600 font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryFilterSection({ activeCategory, setActiveCategory }) {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  return (
    <section id="categories" className="py-12 bg-gray-50" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-8" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>
          {t.browseTitle}
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          {t.cats.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 min-h-[44px] ${
                activeCategory === cat.id
                  ? "btn-premium shadow-lg"
                  : "bg-white text-gray-700 hover:bg-pink-50 hover:text-pink-600 border border-gray-200"
              }`}
            >
              {cat.name} <span className="ml-1 text-sm opacity-75">({cat.count})</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function RemedyCategories({ activeCategory }) {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];
  const titles = CAT_TITLES[lang];

  const filtered = useMemo(
    () => (activeCategory === "all" ? REMEDY_CATEGORIES : REMEDY_CATEGORIES.filter((c) => c.category === activeCategory)),
    [activeCategory]
  );

  const diffColor = (d) =>
    d === "Beginner"
      ? "bg-green-100 text-green-700"
      : d === "Easy"
      ? "bg-blue-100 text-blue-700"
      : "bg-orange-100 text-orange-700";

  const diffLabel = (d) =>
    d === "Beginner" ? t.diffBeginner : d === "Easy" ? t.diffEasy : t.diffMedium;

  const getLocalized = (field) =>
    typeof field === "object" ? field[lang] || field.en : field;

  return (
    <section className="py-16 md:py-20 bg-white" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>
            {t.collTitle}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t.collDesc}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {filtered.map((cat, idx) => (
            <div
              key={idx}
              className={`${cat.bgColor} rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1`}
              data-aos="fade-up"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className={`text-3xl w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.color} shadow-md`}>
                  {cat.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: ctx.headingFont }}>
                    {titles[cat.category]}
                  </h3>
                  <p className="text-gray-600">
                    {cat.remedies.length} {t.remediesAvail}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {cat.remedies.map((remedy, ridx) => (
                  <div key={ridx} className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <h4 className="font-semibold text-gray-900">{getLocalized(remedy.name)}</h4>
                      <div className="flex gap-2 flex-shrink-0">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${diffColor(remedy.difficulty)}`}>
                          {diffLabel(remedy.difficulty)}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium whitespace-nowrap">
                          {getLocalized(remedy.time)}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">{t.ingLabel}:</span> {getLocalized(remedy.ingredients)}
                    </p>
                    <p className="text-sm text-green-700 mb-1">
                      <span className="font-medium">{t.benLabel}:</span> {getLocalized(remedy.benefit)}
                    </p>
                    <p className="text-xs text-gray-500 italic">
                      <span className="font-medium not-italic text-gray-600">{t.howLabel}:</span> {getLocalized(remedy.steps)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PopularRemediesSection() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  return (
    <section id="popular" className="py-16 bg-gradient-to-b from-gray-50 to-white" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>
            {t.popularTitle}
          </h2>
          <p className="text-lg text-gray-600">{t.popularDesc}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {POPULAR_REMEDIES.map((remedy, idx) => (
            <div key={idx} className="card-glass rounded-2xl p-6 flex flex-col" data-aos="fade-up" data-aos-delay={idx * 50}>
              <div className="text-center mb-4">
                <div className="text-5xl mb-3">{remedy.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{remedy.name}</h3>
                <div className="flex items-center justify-center gap-1 mb-2">
                  <div className="flex text-yellow-400 text-sm">{"★".repeat(5)}</div>
                  <span className="text-xs font-medium text-gray-600">{remedy.rating} ({remedy.users})</span>
                </div>
                <span className="inline-block bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-xs font-semibold">{remedy.category}</span>
              </div>
              <div className="mt-auto pt-3 border-t border-gray-100 space-y-1">
                <p className="text-xs text-gray-500">
                  <span className="font-medium text-gray-700">{t.ingLabel}:</span> {remedy.ingredients}
                </p>
                <p className="text-xs text-green-700 font-medium">{remedy.benefit}</p>
                <p className="text-xs text-gray-400">⏱ {remedy.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SeasonalRemediesSection() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  return (
    <section id="seasonal" className="py-16 bg-white" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>
            {t.seasonalTitle}
          </h2>
          <p className="text-lg text-gray-600">{t.seasonalDesc}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {SEASONAL_REMEDIES.map((season, idx) => (
            <div key={idx} className={`${season.bg} rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300`} data-aos="fade-up" data-aos-delay={idx * 100}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`text-3xl w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${season.color} shadow-md`}>
                  {season.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: ctx.headingFont }}>
                  {t.seasons[season.seasonIdx]}
                </h3>
              </div>

              <div className="space-y-3">
                {season.remedies.map((r, ridx) => (
                  <div key={ridx} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="font-semibold text-gray-900 text-sm">{r.name}</h4>
                      <span className="text-xs text-gray-400 whitespace-nowrap ml-2">⏱ {r.time}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{r.ingredients}</p>
                    <p className="text-xs text-green-700 font-medium">{r.benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TipsAndTricksSection() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  return (
    <section className="py-16 bg-gradient-to-b from-pink-50 to-rose-50" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>
            {t.tipsTitle}
          </h2>
          <p className="text-lg text-gray-600">{t.tipsDesc}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.tips.map((tip, idx) => (
            <div key={idx} className="card-glass rounded-xl p-6 transition-all duration-300 hover:-translate-y-1" data-aos="fade-up" data-aos-delay={idx * 50}>
              <div className="text-4xl mb-4">{tip.icon}</div>
              <h3 className="text-lg font-bold mb-3" style={{ color: "#1a1a2e", fontFamily: ctx.headingFont }}>
                {tip.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">{tip.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState("");

  const handleSubscribe = () => {
    if (!email || !email.includes("@")) {
      setError(t.emailErr);
      return;
    }
    setError("");
    setSubscribed(true);
  };

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-pink-50 via-rose-50 to-amber-50 relative overflow-hidden" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(251,207,232,0.2),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(254,215,170,0.2),transparent_50%)]"></div>

      <div className="relative max-w-4xl mx-auto px-4 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>
          {t.ctaTitle}
        </h2>
        <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">{t.ctaDesc}</p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <a
            href={YT_URL}
            target="_blank"
            rel="noreferrer"
            className="px-8 py-4 bg-red-600 text-white rounded-xl text-lg font-medium shadow-lg hover:bg-red-700 hover:-translate-y-0.5 flex items-center gap-2 transition-all duration-300"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            {t.btnSubYT}
          </a>

          <a href={IG_URL_VLOG} target="_blank" rel="noreferrer" className="btn-premium px-8 py-4 rounded-xl text-lg font-medium flex items-center gap-2">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
            {t.btnFollowIG}
          </a>
        </div>

        <div className="card-glass rounded-2xl p-6 max-w-2xl mx-auto">
          <p className="font-semibold text-lg mb-1" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>{t.newsTitle}</p>
          <p className="text-gray-600 text-sm mb-4">{t.newsDesc}</p>

          {subscribed ? (
            <p className="text-green-600 font-semibold py-3">{t.subDone}</p>
          ) : (
            <>
              <div className="flex gap-2 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                  placeholder={t.newsPlaceholder}
                  className="chat-input-field flex-1 rounded-lg"
                />
                <button onClick={handleSubscribe} className="btn-premium px-6 py-3 rounded-lg font-semibold text-sm whitespace-nowrap">
                  {t.btnSub}
                </button>
              </div>
              {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  return (
    <section className="py-16 bg-gray-50" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>{t.testTitle}</h2>
          <p className="text-lg text-gray-600">{t.testDesc}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((te, idx) => (
            <div key={idx} className="card-glass rounded-2xl p-6 flex flex-col" data-aos="fade-up" data-aos-delay={idx * 50}>
              <div className="flex text-yellow-400 mb-3">{"★".repeat(te.rating)}{"☆".repeat(5 - te.rating)}</div>
              <blockquote className="text-gray-700 mb-4 italic flex-1">"{te.text}"</blockquote>
              <div className="border-t pt-4" style={{ borderColor: "rgba(212,168,83,0.2)" }}>
                <div className="font-semibold text-gray-900">{te.name}</div>
                <div className="text-sm text-gray-500">{te.location}</div>
                <div className="text-sm font-medium mt-1" style={{ color: "#c2185b" }}>✅ {t.usedLabel}: {te.remedy}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DisclaimerSection() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  return (
    <div className="py-6 max-w-5xl mx-auto my-8 px-6 text-center text-sm md:text-base text-yellow-900 rounded-2xl border border-yellow-200" style={{ background: "linear-gradient(135deg, #fffbf0, #fef9e7)", fontFamily: ctx.font, direction: ctx.dir }}>
      <p>
        <strong>{t.disclaimerStrong}</strong> {t.disclaimerText}{" "}
        <span className="font-bold text-red-600 underline">{t.patchTest}</span>{" "}
        {t.disclaimerEnd}
      </p>
    </div>
  );
}

function RemediesFooter() {
  const { lang, t: ctx } = useLanguage();
  const t = T[lang];

  return (
    <footer className="bg-gradient-to-b from-gray-50 to-white py-12 border-t border-gray-200" style={{ fontFamily: ctx.font, direction: ctx.dir }}>
      <div className="max-w-6xl mx-auto px-4 text-center">
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: ctx.headingFont, color: "#1a1a2e" }}>{t.siteName}</h3>
        <p className="text-gray-600 mb-6">{t.footerTagline}</p>

        <div className="flex justify-center gap-8 mb-6 flex-wrap">
          <a href={YT_URL} target="_blank" rel="noreferrer" className="text-red-600 hover:text-red-700 font-medium transition-colors">{t.footerYT}</a>
          <a href={IG_URL_MAIN} target="_blank" rel="noreferrer" className="text-pink-500 hover:text-pink-600 font-medium transition-colors">{t.footerIG}</a>
          <Link to="/" className="font-medium transition-colors hover:opacity-80" style={{ color: "#c2185b" }}>{t.footerHome}</Link>
        </div>

        <div className="divider-gold w-32 mx-auto mb-6" />
        <div className="text-sm text-gray-500">
          © {new Date().getFullYear()} Tanya Fashion Skincare • {t.footerCopy}
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function RemediesPage() {
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    AOS.init({ duration: 600, once: true });
    document.title = "Natural Beauty Remedies - DIY Skincare & Hair Care | Tanya Fashion Skincare";
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-warm)" }}>
      <RemediesHeader />
      <HeroSection />
      <QuickStatsSection />
      <CategoryFilterSection activeCategory={activeCategory} setActiveCategory={setActiveCategory} />
      <RemedyCategories activeCategory={activeCategory} />
      <PopularRemediesSection />
      <SeasonalRemediesSection />
      <TipsAndTricksSection />
      <CTASection />
      <TestimonialsSection />
      <DisclaimerSection />
      <RemediesFooter />
    </div>
  );
}